import React, { useState, useRef, useContext, useCallback, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import "./DocumentReader.css";

const ACCEPTED = ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff";
const MAX_SIZE = 15 * 1024 * 1024; // 15MB

const urgencyMeta = {
  High: { label: "High Urgency", className: "dr-urgency-high", icon: "🔴" },
  Medium: { label: "Medium Urgency", className: "dr-urgency-medium", icon: "🟠" },
  Low: { label: "Low Urgency", className: "dr-urgency-low", icon: "🟢" },
};

const fileIconFor = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "tiff"].includes(ext)) return "🖼️";
  if (ext === "pdf") return "📕";
  if (["doc", "docx"].includes(ext)) return "📘";
  if (ext === "txt") return "📄";
  return "📄";
};

const DocumentReader = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useContext(LanguageContext);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  const inputRef = useRef(null);

  // Load available speech-synthesis voices (same approach as FullContent.js)
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;

    const loadVoices = () => {
      const availableVoices = synth.getVoices();
      setVoices(Array.isArray(availableVoices) ? availableVoices : []);
    };

    loadVoices();
    synth.addEventListener("voiceschanged", loadVoices);

    return () => {
      synth.removeEventListener("voiceschanged", loadVoices);
      synth.cancel();
    };
  }, []);

  const getSpeechLanguage = () => {
    if (!selectedLanguage) return "en-US";
    if (selectedLanguage.includes("-")) return selectedLanguage;
    return `${selectedLanguage.toLowerCase()}-${selectedLanguage.toUpperCase()}`;
  };

  const resolveVoiceForLanguage = (langCode) => {
    if (!voices.length) return null;
    const normalized = (langCode || "en-US").toLowerCase();
    const exactMatch = voices.find(
      (voice) => (voice.lang || "").toLowerCase() === normalized
    );
    if (exactMatch) return exactMatch;
    const baseLang = normalized.split("-")[0];
    return (
      voices.find((voice) =>
        (voice.lang || "").toLowerCase().startsWith(`${baseLang}-`)
      ) || null
    );
  };

  const sanitizeTextForSpeech = (text) =>
    String(text || "")
      .replace(/\*+/g, "")
      .replace(/[✅❌⚠️👉🧾🗣️🤝🎥🚨🧠📌🔹📝💡📅🔑📄👨‍⚖️•]/gu, "")
      .replace(/\s{2,}/g, " ")
      .trim();

  // Build a natural narration from the structured result fields.
  const buildSpeechText = (r) => {
    if (!r) return "";
    const parts = [];
    if (r.title) parts.push(r.title);
    if (r.urgency?.level) {
      parts.push(`Urgency level: ${r.urgency.level}.`);
      if (r.urgency.reason) parts.push(r.urgency.reason);
    }
    if (r.simpleSummary) parts.push(r.simpleSummary);
    if (r.whatItMeans) parts.push(r.whatItMeans);
    if (r.detailedExplanation) parts.push(r.detailedExplanation);
    if (Array.isArray(r.keyDates) && r.keyDates.length) {
      parts.push(
        r.keyDates.map((d) => `${d.label}: ${d.date}`).join(". ")
      );
    }
    if (Array.isArray(r.keyPoints) && r.keyPoints.length) {
      parts.push(r.keyPoints.join(". "));
    }
    if (Array.isArray(r.nextActions) && r.nextActions.length) {
      parts.push(r.nextActions.join(". "));
    }
    if (Array.isArray(r.warnings) && r.warnings.length) {
      parts.push(r.warnings.join(". "));
    }
    return parts.join(". ");
  };

  const stopSpeaking = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const handleSpeakToggle = () => {
    const synth = window.speechSynthesis;
    if (!synth) {
      alert("Text-to-speech is not supported in this browser.");
      return;
    }

    if (isSpeaking) {
      stopSpeaking();
      return;
    }

    const speechText = sanitizeTextForSpeech(buildSpeechText(result));
    if (!speechText) return;

    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(speechText);
    const speechLanguage = getSpeechLanguage();
    utterance.lang = speechLanguage;

    const selectedVoice = resolveVoiceForLanguage(speechLanguage);
    if (selectedVoice) utterance.voice = selectedVoice;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synth.speak(utterance);
  };

  const validateAndSet = useCallback((selected) => {
    setError("");
    setResult(null);
    if (!selected) return;
    if (selected.size > MAX_SIZE) {
      setError("File is too large. Maximum size is 15MB.");
      return;
    }
    setFile(selected);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files?.[0];
      validateAndSet(dropped);
    },
    [validateAndSet]
  );

  const handleAnalyze = async () => {
    if (!file) {
      setError("Please choose a document first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    stopSpeaking();

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("language", selectedLanguage || "en");

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.post("/api/document-reader/analyze", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      setResult(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
        return;
      }
      const msg =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Something went wrong while reading your document. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    stopSpeaking();
    setFile(null);
    setResult(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const urgency =
    urgencyMeta[result?.urgency?.level] || urgencyMeta.Medium;

  return (
    <div className="dr-page">
      <div className="dr-header">
        {/* <span className="dr-badge">AI Document Reader</span> */}
        <h1 className="dr-title">Understand any legal document in seconds</h1>
        <p className="dr-subtitle">
          Upload a court notice, legal notice, bank notice, police document or any
          official paper. We'll explain what it means in simple language, tell you
          how urgent it is, and what you should do next.
        </p>
      </div>

      {!result && (
        <div className="dr-upload-wrap">
          <div
            className={`dr-dropzone ${dragOver ? "drag-over" : ""} ${
              file ? "has-file" : ""
            }`}
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              className="dr-file-input"
              onChange={(e) => validateAndSet(e.target.files?.[0])}
            />

            {!file ? (
              <>
                <div className="dr-drop-icon">📤</div>
                <p className="dr-drop-title">
                  Drag &amp; drop your document here
                </p>
                <p className="dr-drop-or">or click to browse</p>
                <p className="dr-drop-formats">
                  Supports PDF, Word, Text and Images (PNG, JPG, etc.)
                </p>
              </>
            ) : (
              <div className="dr-file-chip">
                <span className="dr-file-emoji">{fileIconFor(file.name)}</span>
                <div className="dr-file-info">
                  <span className="dr-file-name">{file.name}</span>
                  <span className="dr-file-size">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <button
                  type="button"
                  className="dr-file-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetAll();
                  }}
                  aria-label="Remove file"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {error && <div className="dr-error">{error}</div>}

          <button
            className="dr-analyze-btn"
            onClick={handleAnalyze}
            disabled={loading || !file}
          >
            {loading ? (
              <>
                <span className="dr-spinner" /> Reading your document...
              </>
            ) : (
              <>✨ Explain this document</>
            )}
          </button>

          {loading && (
            <p className="dr-loading-hint">
              Extracting text and analyzing. This can take a few seconds for
              scanned images.
            </p>
          )}
        </div>
      )}

      {result && (
        <div className="dr-result">
          <div className="dr-result-top">
            <span className="dr-doctype">{result.documentType}</span>
            <h2 className="dr-result-title">{result.title}</h2>

            <div className={`dr-urgency ${urgency.className}`}>
              <span className="dr-urgency-icon">{urgency.icon}</span>
              <div>
                <strong>{urgency.label}</strong>
                {result.urgency?.reason && (
                  <span className="dr-urgency-reason">
                    {result.urgency.reason}
                  </span>
                )}
              </div>
            </div>

            <div className="dr-listen-row">
              <button
                type="button"
                className={`dr-listen-btn ${isSpeaking ? "speaking" : ""}`}
                onClick={handleSpeakToggle}
                aria-label={
                  isSpeaking
                    ? "Stop reading explanation"
                    : "Listen to the explanation"
                }
              >
                {isSpeaking ? (
                  <>
                    <span className="dr-listen-icon">⏹</span> Stop reading
                  </>
                ) : (
                  <>
                    <span className="dr-listen-icon">🔊</span> Listen to explanation
                  </>
                )}
              </button>
              {isSpeaking && (
                <span className="dr-listen-wave" aria-hidden="true">
                  <span /><span /><span /><span />
                </span>
              )}
            </div>
          </div>

          <div className="dr-meta-row">
            {result.fromWho && (
              <div className="dr-meta-pill">
                <span className="dr-meta-label">From</span>
                <span>{result.fromWho}</span>
              </div>
            )}
            {result.aboutWhat && (
              <div className="dr-meta-pill">
                <span className="dr-meta-label">About</span>
                <span>{result.aboutWhat}</span>
              </div>
            )}
          </div>

          <div className="dr-result-body">
          <div className="dr-result-main">

          {result.simpleSummary && (
            <div className="dr-card dr-card-summary">
              <h3>📝 In simple words</h3>
              <p>{result.simpleSummary}</p>
            </div>
          )}

          {result.whatItMeans && (
            <div className="dr-card">
              <h3>💡 What this means for you</h3>
              <p>{result.whatItMeans}</p>
            </div>
          )}

          {result.partiesInvolved?.length > 0 && (
            <div className="dr-card">
              <h3>👥 Who is involved</h3>
              <ul className="dr-parties">
                {result.partiesInvolved.map((p, i) => (
                  <li key={i}>
                    <span className="dr-party-role">{p.role}</span>
                    <span className="dr-party-name">{p.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.keyDates?.length > 0 && (
            <div className="dr-card">
              <h3>📅 Important dates &amp; deadlines</h3>
              <ul className="dr-dates">
                {result.keyDates.map((d, i) => (
                  <li key={i}>
                    <span className="dr-date-label">{d.label}</span>
                    <span className="dr-date-value">{d.date}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.keyPoints?.length > 0 && (
            <div className="dr-card">
              <h3>🔑 Key points</h3>
              <ul className="dr-list">
                {result.keyPoints.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          {result.glossary?.length > 0 && (
            <div className="dr-card">
              <h3>📚 Legal terms explained</h3>
              <dl className="dr-glossary">
                {result.glossary.map((g, i) => (
                  <div className="dr-glossary-item" key={i}>
                    <dt>{g.term}</dt>
                    <dd>{g.meaning}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {result.nextActions?.length > 0 && (
            <div className="dr-card dr-card-actions">
              <h3>✅ What you should do next</h3>
              <ol className="dr-steps">
                {result.nextActions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ol>
            </div>
          )}

          {result.warnings?.length > 0 && (
            <div className="dr-card dr-card-warning">
              <h3>⚠️ Important warnings</h3>
              <ul className="dr-list">
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          </div>{/* /dr-result-main */}

          {result.detailedExplanation && (
            <aside className="dr-result-aside">
              <div className="dr-card dr-card-detailed">
                <h3>📖 Detailed explanation</h3>
                {result.detailedExplanation
                  .split(/\n{2,}|\n/)
                  .map((para) => para.trim())
                  .filter(Boolean)
                  .map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
              </div>
            </aside>
          )}

          </div>{/* /dr-result-body */}

          {result.shouldConsultLawyer && (
            <div className="dr-lawyer-cta">
              <span>👨‍⚖️</span>
              <p>
                This matter may need professional help. Consider consulting a
                qualified lawyer.
              </p>
            </div>
          )}

          <p className="dr-disclaimer">{result.disclaimer}</p>

          <div className="dr-result-buttons">
            <button className="dr-secondary-btn" onClick={resetAll}>
              📄 Read another document
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentReader;
